import { Input } from '@tonic/shared/components/ui';
import { Button } from '@tonic/shared/components/ui';
import { Card, CardContent } from '@tonic/shared/components/ui';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from '@tonic/shared/components/ui';
import { Search, X } from 'lucide-react';
import PropTypes from 'prop-types';

export function ProductsFilter({ filters, onFilterChange, onReset }) {
  const handleSearchChange = (value) => {
    onFilterChange({ ...filters, search: value });
  };

  const handleStatusChange = (value) => {
    onFilterChange({ 
      ...filters, 
      status: value
    });
  };

  const handleReCommerceChange = (key, value) => {
    onFilterChange({
      ...filters,
      reCommerceFlags: {
        ...filters.reCommerceFlags,
        [key]: value
      }
    });
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex flex-col gap-4 md:flex-row">
          <div className="flex-1 space-y-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search by SKU, Product Name, or Category..." 
                className="pl-8"
                value={filters.search || ''}
                onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
              />
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-4 md:flex-row">
            <div className="flex-1 space-y-2">
              <Select 
                value={filters.status || 'all'}
                onValueChange={(value) => onFilterChange({ 
                  ...filters, 
                  status: value === 'all' ? undefined : value
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="discontinued">Discontinued</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 space-y-2">
              <Select
                value={filters.isVisibleInCommerce === undefined ? 'all' : filters.isVisibleInCommerce.toString()}
                onValueChange={(value) => onFilterChange({
                  ...filters,
                  isVisibleInCommerce: value === 'all' ? undefined : value === 'true'
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Visibility</SelectItem>
                  <SelectItem value="true">Visible in Commerce</SelectItem>
                  <SelectItem value="false">Hidden in Commerce</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1 space-y-2">
              <Button 
                variant="ghost" 
                onClick={onReset}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-6 mt-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.reCommerceFlags?.eligibleForResale || false}
              onChange={(e) => handleReCommerceChange('eligibleForResale', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Resale Eligible</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.reCommerceFlags?.eligibleForRepair || false}
              onChange={(e) => handleReCommerceChange('eligibleForRepair', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Repair Eligible</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.reCommerceFlags?.eligibleForRental || false}
              onChange={(e) => handleReCommerceChange('eligibleForRental', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Rental Eligible</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={filters.reCommerceFlags?.isRefillable || false}
              onChange={(e) => handleReCommerceChange('isRefillable', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm">Refillable</span>
          </label>
        </div>
      </CardContent>
    </Card>
  );
}

ProductsFilter.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    status: PropTypes.oneOf(['active', 'discontinued', 'draft']),
    isVisibleInCommerce: PropTypes.bool,
    reCommerceFlags: PropTypes.shape({
      eligibleForResale: PropTypes.bool,
      eligibleForRepair: PropTypes.bool,
      eligibleForRental: PropTypes.bool,
      isRefillable: PropTypes.bool
    })
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
}; 